import { useMemo } from "react";
import { NetworkStatus, useQuery } from "@apollo/client";
import type { Course, CubeQuery } from "@models/ISchema";
import populationCoursesQuery from "../../graphql/populationCourses.query.graphql";

interface UsePopulationCoursesProps {
  scenarioId: string;
  originId: string;
  termId: string;
  populationCode: string;
  enabled?: boolean;
}

type PopulationCoursesQueryData = {
  cube?: Pick<CubeQuery, "populationCourses">;
};

const isCourse = (course: Course | null | undefined): course is Course => Boolean(course);

export const usePopulationCourses = ({
  scenarioId,
  originId,
  termId,
  populationCode,
  enabled = true,
}: UsePopulationCoursesProps) => {
  const shouldSkip = !enabled || !scenarioId || !originId || !termId || !populationCode;
  const { data, loading, error, refetch, networkStatus } = useQuery<PopulationCoursesQueryData>(
    populationCoursesQuery,
    {
      variables: { scenarioId, originId, termId, populationCode },
      skip: shouldSkip,
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    },
  );

  const courses = useMemo(
    () => (data?.cube?.populationCourses ?? []).filter(isCourse),
    [data?.cube?.populationCourses],
  );

  return {
    courses,
    isLoading: loading,
    isRetrying: networkStatus === NetworkStatus.refetch,
    error,
    isEmpty: !shouldSkip && !loading && !error && courses.length === 0,
    retry: refetch,
  };
};

export default usePopulationCourses;
